// Wrong-blueprint detection (blueprint-sets Req 2.8, 5.9, 5.10; design Decision 9, "sets/coverage.ts").
// Probe P2: BDA matched documents with no fitting Member to the closest Member at high confidence; the only signal
// was how few of that Member's fields came back with a value. Pure and deterministic: completion (API) stores
// `fieldCoverage` per matched segment, and the browser and the download builders compare it with the run's snapshot
// `coverageFloor`. The Possible_Mismatch flag itself is never stored.
import { z } from 'zod';
/** Req 2.8: default Coverage_Floor of a set (a share, 0..1; 0 turns the check off). */
export const DEFAULT_COVERAGE_FLOOR = 0.3;
/** Req 2.8: bounds of a Coverage_Floor; also used by the set request, response, and snapshot schemas in `api.ts`. */
export const coverageFloorSchema = z.number().min(0).max(1);
/** No value: null, missing, blank text, or an empty list. */
export const isEmptyValue = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
function kindOf(p) {
    if (isRecord(p) && p.$ref !== undefined)
        return 'group';
    if (isRecord(p) && p.type === 'array' && isRecord(p.items) && p.items.$ref !== undefined)
        return 'table';
    return 'field';
}
/**
 * Req 5.9: Field_Coverage of one matched segment against its Matched_Member's schema. `total` is the number of
 * top-level properties of the schema (scalars, lists, groups, tables); `found` counts those with a value: a scalar or
 * list that is not empty (`isEmptyValue`), a group with at least one non-empty field (`group.field` paths), a table
 * with at least one row that has a non-empty cell.
 */
export function fieldCoverageOf(schema, segment) {
    const props = isRecord(schema?.properties) ? schema.properties : {};
    let found = 0;
    let total = 0;
    for (const [name, prop] of Object.entries(props)) {
        total++;
        const kind = kindOf(prop);
        const has = kind === 'table'
            ? segment.tables.some((t) => t.name === name && t.rows.some((r) => Object.values(r).some((c) => !isEmptyValue(c?.value))))
            : kind === 'group'
                ? segment.fields.some((f) => f.path.startsWith(`${name}.`) && !isEmptyValue(f.value))
                : segment.fields.some((f) => f.path === name && !isEmptyValue(f.value));
        if (has)
            found++;
    }
    return { found, total };
}
/**
 * Req 5.10: a matched segment whose Field_Coverage share is below the floor. A No_Match_Segment, a segment without
 * `fieldCoverage` (older results), `total === 0`, or `floor === 0` is never a mismatch.
 */
export function isPossibleMismatch(seg, floor) {
    const c = seg.fieldCoverage;
    return (seg.matchedMember !== null &&
        seg.matchedMember !== undefined &&
        c !== null &&
        c !== undefined &&
        c.total > 0 &&
        c.found / c.total < floor);
}
//# sourceMappingURL=coverage.js.map